import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { cn } from "@/lib/utils";
import { Loader2, AlertCircle, Clock } from "lucide-react";
import {
  useAdHocEntryBusinessUnits,
  type AdHocEntryBusinessUnit,
} from "../hooks/useAdHocEntryBusinessUnits";
import { useOrganizationWageSetsForAdHocEntry } from "../hooks/useOrganizationWageSetsForAdHocEntry";
import { useCreateAdHocTimeEntry } from "../hooks/useCreateAdHocTimeEntry";
import {
  adHocTimeEntryFormSchema,
  wageTypeOptions,
  getMaxDateForAdHoc,
  getMinDateForAdHoc,
  combineDateTimeForAdHoc,
  TimesheetEntryBaseWageType,
  type AdHocTimeEntryFormInput,
} from "../utils/ad-hoc-time-entry-schema";
import {
  DEFAULT_CUSTOM_WAGE_FREQUENCY,
  type CustomWage,
} from "../utils/custom-wage";
import { CustomWageFields } from "./CustomWageFields";
import {
  orgWageSetAllowsOvertime,
  getWageTypeOptionsForOrgWageSet,
} from "../utils/ad-hoc-organization-wage-set";
import { AdHocOrganizationWageSetField } from "./ad-hoc-time-entry-dialog/AdHocOrganizationWageSetField";
import { AdHocPaySummary } from "./ad-hoc-time-entry-dialog/AdHocPaySummary";
import { getAdHocPayPreview } from "../utils/ad-hoc-pay-preview";
interface AdHocTimeEntryDialogProps {
  children?: React.ReactNode;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

const STANDARD_WAGE_DESCRIPTION = "Paid at the rate in your wage set.";
const CUSTOM_WAGE_DESCRIPTION =
  "Enter your own rate. The organization reviews it before approving and may change it.";

enum WageChoice {
  Standard = "standard",
  Custom = "custom",
}

export function AdHocTimeEntryDialog({
  children,
  open: controlledOpen,
  onOpenChange: controlledOnOpenChange,
}: AdHocTimeEntryDialogProps) {
  const [open, setOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { businessUnits, isLoading: loadingBusinessUnits } =
    useAdHocEntryBusinessUnits();
  const { mutateAsync: createEntry, isPending: isSubmitting } =
    useCreateAdHocTimeEntry();

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
    reset,
  } = useForm<AdHocTimeEntryFormInput>({
    resolver: zodResolver(adHocTimeEntryFormSchema),
    // Empty strings keep the selects controlled from the first render; the
    // schema still rejects them until a choice is made.
    defaultValues: {
      organizationBusinessUnitId: "",
      facilityId: "",
      organizationWageSetId: "",
      baseWageType: TimesheetEntryBaseWageType.Regular,
      isOvertime: false,
      useCustomWage: false,
      customWageAmount: "",
      customWageFrequency: DEFAULT_CUSTOM_WAGE_FREQUENCY,
    },
  });

  const isOpen = controlledOpen !== undefined ? controlledOpen : open;
  const setIsOpen = controlledOnOpenChange || setOpen;

  const selectedBusinessUnitId = watch("organizationBusinessUnitId");
  const selectedFacilityId = watch("facilityId");
  const selectedOrganizationWageSetId = watch("organizationWageSetId");
  const date = watch("date");
  const startTime = watch("startsAt");
  const endTime = watch("endsAt");
  const selectedWageType = watch("baseWageType");
  const isOvertime = watch("isOvertime");
  const useCustomWage = watch("useCustomWage");
  const customWageAmount = watch("customWageAmount");
  const customWageFrequency = watch("customWageFrequency");

  const selectedBusinessUnit = businessUnits.find(
    (unit) => unit.organizationBusinessUnitId === selectedBusinessUnitId,
  );
  const timezoneId = selectedBusinessUnit?.timezoneId;

  // The date and time entered are wall-clock values at the business unit, so
  // interpret them in its timezone rather than the browser's. The wage sets
  // in effect depend on this start instant.
  const startsAtInstant = useMemo(() => {
    if (!timezoneId || !date || !startTime) {
      return undefined;
    }
    const instant = combineDateTimeForAdHoc(date, startTime, timezoneId);
    return instant ? new Date(instant) : undefined;
  }, [timezoneId, date, startTime]);

  // Same overnight handling as submit, so the summary prices what is sent.
  const endsAtInstant = useMemo(() => {
    if (!timezoneId || !date || !startTime || !endTime) {
      return undefined;
    }
    const instant = combineDateTimeForAdHoc(
      date,
      endTime,
      timezoneId,
      true,
      startTime,
    );
    return instant ? new Date(instant) : undefined;
  }, [timezoneId, date, startTime, endTime]);

  const { organizationWageSets, isLoading: loadingOrganizationWageSets } =
    useOrganizationWageSetsForAdHocEntry(
      selectedBusinessUnitId && startsAtInstant
        ? {
            organizationBusinessUnitId: selectedBusinessUnitId,
            at: startsAtInstant,
          }
        : undefined,
    );
  const hasOrganizationWageSetLookup =
    !!startsAtInstant && !!selectedBusinessUnit;
  const selectedOrganizationWageSet = organizationWageSets.find(
    (orgWageSet) =>
      orgWageSet.organizationWageSetId === selectedOrganizationWageSetId,
  );
  const hasNoApplicableOrgWageSet =
    hasOrganizationWageSetLookup &&
    !loadingOrganizationWageSets &&
    organizationWageSets.length === 0;

  const payPreview = getAdHocPayPreview({
    wages: selectedOrganizationWageSet?.wages,
    wageType: selectedWageType,
    isOvertime,
    customWage: useCustomWage
      ? { amount: customWageAmount, wageFrequencyId: customWageFrequency }
      : null,
    startsAt: startsAtInstant,
    endsAt: endsAtInstant,
  });

  const allowedWageTypeOptions =
    useCustomWage || !selectedOrganizationWageSet
      ? wageTypeOptions
      : getWageTypeOptionsForOrgWageSet(selectedOrganizationWageSet.wages);
  // The server rejects overtime with a custom wage or on a per-shift wage set, and
  // needs the wage set to have an overtime wage for the selected type.
  const showsOvertime =
    !useCustomWage &&
    !!selectedOrganizationWageSet &&
    orgWageSetAllowsOvertime({
      wages: selectedOrganizationWageSet.wages,
      wageType: selectedWageType,
    });

  useEffect(() => {
    if (organizationWageSets.length === 1) {
      setValue(
        "organizationWageSetId",
        organizationWageSets[0].organizationWageSetId,
      );
      return;
    }
    if (
      selectedOrganizationWageSetId &&
      !selectedOrganizationWageSet &&
      !loadingOrganizationWageSets
    ) {
      // The date or organization changed, so the chosen wage set no longer applies.
      setValue("organizationWageSetId", "");
    }
  }, [
    organizationWageSets,
    selectedOrganizationWageSetId,
    selectedOrganizationWageSet,
    loadingOrganizationWageSets,
    setValue,
  ]);

  useEffect(() => {
    // A changed start time briefly removes the query's data. Only resolved
    // pay terms (or a custom wage) can invalidate the provider's overtime choice.
    if (!useCustomWage && (!selectedOrganizationWageSet || showsOvertime)) {
      return;
    }

    setValue("isOvertime", false);
  }, [useCustomWage, selectedOrganizationWageSet, showsOvertime, setValue]);

  useEffect(() => {
    // With a custom wage the wage type only describes the work, so any type
    // is fine. Switching back to the standard wage re-runs this and coerces it.
    if (!selectedOrganizationWageSet || useCustomWage) {
      return;
    }
    const isAllowed = getWageTypeOptionsForOrgWageSet(
      selectedOrganizationWageSet.wages,
    ).some((option) => option.value === selectedWageType);
    if (isAllowed) {
      return;
    }

    setValue("baseWageType", TimesheetEntryBaseWageType.Regular);
  }, [selectedOrganizationWageSet, selectedWageType, useCustomWage, setValue]);

  const handleBusinessUnitChange = (businessUnitId: string) => {
    const unit = businessUnits.find(
      (candidate) => candidate.organizationBusinessUnitId === businessUnitId,
    );
    setValue("organizationBusinessUnitId", businessUnitId, {
      shouldValidate: !!errors.organizationBusinessUnitId,
    });
    setValue("organizationWageSetId", "");
    // A single facility needs no choice.
    setValue(
      "facilityId",
      unit?.facilities.length === 1 ? unit.facilities[0].id : "",
    );
  };

  const onSubmit = async (data: AdHocTimeEntryFormInput) => {
    setError(null);

    if (!selectedBusinessUnit) {
      setError("Select an organization before submitting hours");
      return;
    }

    try {
      const entryTimezoneId = selectedBusinessUnit.timezoneId;
      const startsAt = combineDateTimeForAdHoc(
        data.date,
        data.startsAt,
        entryTimezoneId,
      );
      const endsAt = combineDateTimeForAdHoc(
        data.date,
        data.endsAt,
        entryTimezoneId,
        true,
        data.startsAt,
      );

      const customWage: CustomWage | undefined = data.useCustomWage
        ? {
            amount: data.customWageAmount.trim(),
            wageFrequencyId: data.customWageFrequency,
          }
        : undefined;

      await createEntry({
        organizationBusinessUnitId: data.organizationBusinessUnitId,
        facilityId: data.facilityId,
        organizationWageSetId: data.organizationWageSetId,
        startsAt: new Date(startsAt),
        endsAt: new Date(endsAt),
        baseWageType: data.baseWageType,
        isOvertime: showsOvertime ? data.isOvertime : false,
        customWage,
        providerNote: data.providerNote,
      });

      reset();
      setIsOpen(false);
    } catch (err) {
      console.error("Failed to create ad-hoc time entry:", err);
      setError(
        err instanceof Error ? err.message : "Failed to submit time entry",
      );
    }
  };

  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) {
      reset();
      setError(null);
    }
    setIsOpen(newOpen);
  };

  const maxDate = timezoneId ? getMaxDateForAdHoc(timezoneId) : undefined;
  const minDate = timezoneId ? getMinDateForAdHoc(timezoneId) : undefined;
  const hasNoBusinessUnits =
    !loadingBusinessUnits && businessUnits.length === 0;

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      {children && <DialogTrigger asChild>{children}</DialogTrigger>}
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Clock className="h-5 w-5" />
            Add Hours
          </DialogTitle>
          <DialogDescription>
            Submit hours for work that wasn't on your schedule, including shifts
            that ended earlier today. Date must be within the last 30 days.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          {/* Organization Selection */}
          <div className="space-y-2">
            <Label htmlFor="organizationBusinessUnitId">
              Organization <span className="text-red-500">*</span>
            </Label>
            {loadingBusinessUnits && (
              <div className="flex items-center gap-2 text-sm text-slate-500">
                <Loader2 className="h-4 w-4 animate-spin" />
                Loading organizations...
              </div>
            )}
            {hasNoBusinessUnits && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  You can add hours once an organization hires you and assigns
                  you a wage set.
                </AlertDescription>
              </Alert>
            )}
            {businessUnits.length > 0 && (
              <Select
                value={selectedBusinessUnitId}
                onValueChange={handleBusinessUnitChange}
                disabled={isSubmitting}
              >
                <SelectTrigger
                  id="organizationBusinessUnitId"
                  className="h-auto min-h-[56px] py-2 [&>span]:line-clamp-none"
                >
                  <SelectValue placeholder="Select an organization">
                    {selectedBusinessUnit && (
                      <BusinessUnitOptionLabel unit={selectedBusinessUnit} />
                    )}
                  </SelectValue>
                </SelectTrigger>
                <SelectContent>
                  {businessUnits.map((unit) => (
                    <SelectItem
                      key={unit.organizationBusinessUnitId}
                      value={unit.organizationBusinessUnitId}
                    >
                      <BusinessUnitOptionLabel unit={unit} />
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            {errors.organizationBusinessUnitId && (
              <p className="text-sm text-red-600">
                {errors.organizationBusinessUnitId.message}
              </p>
            )}
          </div>

          {selectedBusinessUnit && (
            <>
              {/* Facility */}
              <div className="space-y-2">
                <Label htmlFor="facilityId">
                  Facility <span className="text-red-500">*</span>
                </Label>
                <Select
                  value={selectedFacilityId}
                  onValueChange={(value) =>
                    setValue("facilityId", value, {
                      shouldValidate: !!errors.facilityId,
                    })
                  }
                  disabled={isSubmitting}
                >
                  <SelectTrigger id="facilityId">
                    <SelectValue placeholder="Select a facility" />
                  </SelectTrigger>
                  <SelectContent>
                    {selectedBusinessUnit.facilities.map((facility) => (
                      <SelectItem key={facility.id} value={facility.id}>
                        <span className="flex flex-col items-start text-left">
                          <span>{facility.name}</span>
                          {facility.city && facility.state && (
                            <span className="text-xs text-slate-500">
                              {facility.city}, {facility.state}
                            </span>
                          )}
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {errors.facilityId && (
                  <p className="text-sm text-red-600">
                    {errors.facilityId.message}
                  </p>
                )}
              </div>

              {/* Date */}
              <div className="space-y-2">
                <Label htmlFor="date">
                  Date <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="date"
                  type="date"
                  max={maxDate}
                  min={minDate}
                  disabled={isSubmitting}
                  {...register("date")}
                />
                {errors.date && (
                  <p className="text-sm text-red-600">{errors.date.message}</p>
                )}
              </div>

              {/* Time Range */}
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label htmlFor="startsAt">
                    Start Time <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="startsAt"
                    type="time"
                    disabled={isSubmitting}
                    {...register("startsAt")}
                  />
                  {errors.startsAt && (
                    <p className="text-sm text-red-600">
                      {errors.startsAt.message}
                    </p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="endsAt">
                    End Time <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="endsAt"
                    type="time"
                    disabled={isSubmitting}
                    {...register("endsAt")}
                  />
                  {errors.endsAt && (
                    <p className="text-sm text-red-600">
                      {errors.endsAt.message}
                    </p>
                  )}
                </div>
              </div>

              {/* Wage Set */}
              {hasOrganizationWageSetLookup && (
                <AdHocOrganizationWageSetField
                  organizationWageSets={organizationWageSets}
                  isLoading={loadingOrganizationWageSets}
                  organizationName={selectedBusinessUnit.organizationName}
                  value={selectedOrganizationWageSetId}
                  onChange={(organizationWageSetId) =>
                    setValue("organizationWageSetId", organizationWageSetId, {
                      shouldValidate: !!errors.organizationWageSetId,
                    })
                  }
                  disabled={isSubmitting}
                  error={errors.organizationWageSetId?.message}
                />
              )}

              {selectedOrganizationWageSet && (
                <>
                  {/* Wage */}
                  <div className="space-y-2">
                    <Label id="wage-choice-label">Wage</Label>
                    <RadioGroup
                      aria-labelledby="wage-choice-label"
                      value={
                        useCustomWage ? WageChoice.Custom : WageChoice.Standard
                      }
                      onValueChange={(value) =>
                        setValue("useCustomWage", value === WageChoice.Custom)
                      }
                      disabled={isSubmitting}
                    >
                      <WageChoiceOption
                        id="wage-standard"
                        value={WageChoice.Standard}
                        title="Standard wage"
                        description={STANDARD_WAGE_DESCRIPTION}
                        isSelected={!useCustomWage}
                      />
                      <WageChoiceOption
                        id="wage-custom"
                        value={WageChoice.Custom}
                        title="Custom wage"
                        description={CUSTOM_WAGE_DESCRIPTION}
                        isSelected={useCustomWage}
                      >
                        <CustomWageFields
                          idPrefix="ad-hoc-custom-wage"
                          isLabelHidden
                          amount={customWageAmount}
                          frequency={customWageFrequency}
                          onAmountChange={(amount) =>
                            setValue("customWageAmount", amount, {
                              shouldValidate: !!errors.customWageAmount,
                            })
                          }
                          onFrequencyChange={(frequency) =>
                            setValue("customWageFrequency", frequency)
                          }
                          error={errors.customWageAmount?.message}
                          disabled={isSubmitting}
                        />
                      </WageChoiceOption>
                    </RadioGroup>
                  </div>

                  {/* Wage Type */}
                  <div className="space-y-2">
                    <Label htmlFor="baseWageType">
                      Wage Type <span className="text-red-500">*</span>
                    </Label>
                    <Select
                      value={selectedWageType}
                      onValueChange={(value) =>
                        setValue(
                          "baseWageType",
                          value as AdHocTimeEntryFormInput["baseWageType"],
                        )
                      }
                      disabled={isSubmitting}
                    >
                      <SelectTrigger id="baseWageType">
                        <SelectValue placeholder="Select wage type" />
                      </SelectTrigger>
                      <SelectContent>
                        {allowedWageTypeOptions.map((option) => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {errors.baseWageType && (
                      <p className="text-sm text-red-600">
                        {errors.baseWageType.message}
                      </p>
                    )}
                  </div>

                  {/* Overtime Checkbox */}
                  {showsOvertime && (
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="isOvertime"
                        checked={isOvertime}
                        onCheckedChange={(checked) =>
                          setValue("isOvertime", checked === true)
                        }
                        disabled={isSubmitting}
                      />
                      <Label
                        htmlFor="isOvertime"
                        className="text-sm font-normal cursor-pointer"
                      >
                        This is overtime
                      </Label>
                    </div>
                  )}
                </>
              )}

              {/* Provider Note */}
              <div className="space-y-2">
                <Label htmlFor="providerNote">Notes (Optional)</Label>
                <Textarea
                  id="providerNote"
                  placeholder="Add any details about this work..."
                  rows={3}
                  disabled={isSubmitting}
                  {...register("providerNote")}
                />
              </div>

              {payPreview && (
                <AdHocPaySummary
                  preview={payPreview}
                  isCustomWage={useCustomWage}
                />
              )}
            </>
          )}

          {/* Error Display */}
          {error && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {/* Submit Buttons */}
          <div className="flex items-center justify-end gap-3 pt-4 border-t">
            <Button
              type="button"
              variant="outline"
              onClick={() => handleOpenChange(false)}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={
                isSubmitting ||
                !selectedBusinessUnit ||
                hasNoApplicableOrgWageSet ||
                !selectedOrganizationWageSet
              }
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Submitting...
                </>
              ) : (
                "Submit Hours"
              )}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function BusinessUnitOptionLabel({ unit }: { unit: AdHocEntryBusinessUnit }) {
  return (
    <span className="flex min-w-0 flex-col items-start text-left">
      <span className="max-w-full truncate">{unit.organizationName}</span>
      <span className="max-w-full truncate text-xs text-slate-500">
        {unit.businessUnitName}
      </span>
    </span>
  );
}

/**
 * A selectable card for one wage choice. The whole header is tappable, and
 * `children` (the custom wage inputs) show inside the card only while it is
 * selected. aria-labelledby keeps the radio's accessible name to the title so
 * the description is not read as part of the name.
 */
function WageChoiceOption({
  id,
  value,
  title,
  description,
  isSelected,
  children,
}: {
  id: string;
  value: WageChoice;
  title: string;
  description: string;
  isSelected: boolean;
  children?: React.ReactNode;
}) {
  const titleId = `${id}-title`;
  const descriptionId = `${id}-description`;

  return (
    <div
      className={cn(
        "rounded-md border p-3 transition-colors",
        isSelected ? "border-slate-400 bg-slate-50" : "hover:bg-slate-50",
      )}
    >
      <Label
        htmlFor={id}
        className="flex cursor-pointer items-start gap-3 font-normal"
      >
        <RadioGroupItem
          id={id}
          value={value}
          className="mt-0.5"
          aria-labelledby={titleId}
          aria-describedby={descriptionId}
        />
        <span className="flex min-w-0 flex-col gap-0.5">
          <span id={titleId} className="text-sm font-medium text-slate-900">
            {title}
          </span>
          <span id={descriptionId} className="text-xs text-slate-500">
            {description}
          </span>
        </span>
      </Label>
      {isSelected && children && <div className="mt-3">{children}</div>}
    </div>
  );
}
